import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Suspense } from 'react';
import { Layout } from './components/Layout';
import { AuthLayout } from './components/AuthLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PublicOrProtectedLayout } from './components/PublicOrProtectedLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { lazyRetry } from '@/utils/lazyRetry';
import { useAuth } from '@/hooks/useAuth';

// lazyRetry оборачивает React.lazy с авторетраем при сбое загрузки чанка
// (типично после деплоя — см. utils/lazyRetry.ts). ErrorBoundary ниже ловит
// то, что всё же не загрузилось, и не даёт получить белый экран при навигации.
const Login = lazyRetry(() => import('./pages/auth/Login').then(m => ({ default: m.Login })), 'Login');
const Register = lazyRetry(() => import('./pages/auth/Register').then(m => ({ default: m.Register })), 'Register');
const OAuthCallback = lazyRetry(() => import('./pages/auth/OAuthCallback').then(m => ({ default: m.OAuthCallback })), 'OAuthCallback');
const ForgotPassword = lazyRetry(() => import('./pages/auth/ForgotPassword').then(m => ({ default: m.ForgotPassword })), 'ForgotPassword');
const VerifyEmail = lazyRetry(() => import('./pages/auth/VerifyEmail').then(m => ({ default: m.VerifyEmail })), 'VerifyEmail');
const ResetPassword = lazyRetry(() => import('./pages/auth/ResetPassword').then(m => ({ default: m.ResetPassword })), 'ResetPassword');

const Dashboard = lazyRetry(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })), 'Dashboard');
const PublicHome = lazyRetry(() => import('./pages/PublicHome').then(m => ({ default: m.PublicHome })), 'PublicHome');
const Profile = lazyRetry(() => import('./pages/Profile').then(m => ({ default: m.Profile })), 'Profile');
const Organizations = lazyRetry(() => import('./pages/organizations/Organizations').then(m => ({ default: m.Organizations })), 'Organizations');
const OrganizationDetail = lazyRetry(() => import('./pages/organizations/OrganizationDetail').then(m => ({ default: m.OrganizationDetail })), 'OrganizationDetail');
const CreateOrganization = lazyRetry(() => import('./pages/organizations/CreateOrganization').then(m => ({ default: m.CreateOrganization })), 'CreateOrganization');
const Venues = lazyRetry(() => import('./pages/venues/Venues').then(m => ({ default: m.Venues })), 'Venues');
const AllVenues = lazyRetry(() => import('./pages/venues/AllVenues').then(m => ({ default: m.AllVenues })), 'AllVenues');
const Queues = lazyRetry(() => import('./pages/queues/Queues').then(m => ({ default: m.Queues })), 'Queues');
const Complaints = lazyRetry(() => import('./pages/complaints/Complaints').then(m => ({ default: m.Complaints })), 'Complaints');
const Settings = lazyRetry(() => import('./pages/Settings').then(m => ({ default: m.Settings })), 'Settings');
const NotFound = lazyRetry(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })), 'NotFound');

const LoadingFallback = () => <LoadingSpinner size="lg" className="mx-auto my-20" />;

/**
 * Схлопывает ведущие множественные слэши в pathname (//auth/callback → /auth/callback).
 *
 * Зачем: бэкенд-редиректы OAuth иногда формируют URL вида
 * http://localhost:3000//auth/callback (двойной слэш). React Router v6 такой
 * маршрут не сматчивает → пользователь видит страницу NotFound ("404").
 * Обёртка ловит такие пути и делает клиентский редирект на нормализованный.
 */
function NormalizeSlashes({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { pathname } = location;
  if (pathname.length > 1 && pathname.startsWith('//')) {
    const normalized = '/' + pathname.replace(/^\/+/, '');
    return <Navigate to={`${normalized}${location.search}${location.hash}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Маршрут «/»: авторизованному — редирект на /dashboard, анониму — публичный
 * лендинг (PublicHome). Раньше «/» всегда редиректил на /dashboard, из-за чего
 * аноним моментально улетал на /auth/login и не мог видеть публичные сущности.
 */
function HomeOrDashboard() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner size="lg" className="mx-auto my-20" />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <PublicHome />;
}

export function App() {
  return (
    <NormalizeSlashes>
    <ErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="callback" element={<OAuthCallback />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="verify-email" element={<VerifyEmail />} />
          <Route path="reset-password" element={<ResetPassword />} />
        </Route>

        {/* Read-only маршруты: доступны и анонимам, и авторизованным.
            PublicOrProtectedLayout сам выбирает Layout (полный) или PublicLayout
            (компактный хедер) по isAuthenticated. Сами страницы скрывают действия
            для анонимов. Действия (dashboard/profile/create/complaints/settings)
            остаются под ProtectedRoute ниже. */}
        <Route element={<PublicOrProtectedLayout />}>
          {/* Аноним видит публичный лендинг (PublicHome), авторизованный — редирект на dashboard. */}
          <Route path="/" element={<HomeOrDashboard />} />
          <Route path="/home" element={<PublicHome />} />

          <Route path="organizations" element={<Organizations />} />
          <Route path="organizations/:id" element={<OrganizationDetail />} />

          <Route path="organizations/:organizationId/venues" element={<Venues />} />

          <Route path="venues" element={<AllVenues />} />

          <Route path="queues" element={<Queues />} />
        </Route>

        {/* Action-маршруты: только для авторизованных (полный Layout). */}
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="dashboard" element={<Dashboard />} />

          <Route path="profile" element={<Profile />} />

          <Route path="organizations/create" element={<CreateOrganization />} />

          <Route path="complaints" element={<Complaints />} />

          <Route path="settings" element={<Settings />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
    </NormalizeSlashes>
  );
}